import { useState, useEffect, useCallback, useRef } from "react";
import { useFaust } from "../../contexts/FaustContext";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Key2Midi } from "../../Key2Midi";

export function MIDIInput() {
    const { audioEnvRef } = useFaust();
    const [midiSupported, setMidiSupported] = useState(false);
    const [devices, setDevices] = useState<{ id: string; name: string }[]>([]);
    const [selectedDevice, setSelectedDevice] = useState("-2"); // Computer keyboard default
    const [currentNote, setCurrentNote] = useState<number | null>(null);
    const key2MidiRef = useRef<Key2Midi | null>(null);
    const midiInputRef = useRef<any>(null);

    useEffect(() => {
        // Initialize keyboard MIDI
        const keyMap = navigator.language === "fr-FR" ? Key2Midi.KEY_MAP_FR : Key2Midi.KEY_MAP;
        key2MidiRef.current = new Key2Midi({ keyMap, enabled: true });

        // Set up keyboard event listeners
        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't capture keys when editor is focused
            const activeElement = document.activeElement;
            if (activeElement?.closest(".monaco-editor")) return;
            key2MidiRef.current?.handleKeyDown(e.key);
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            const activeElement = document.activeElement;
            if (activeElement?.closest(".monaco-editor")) return;
            key2MidiRef.current?.handleKeyUp(e.key);
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("keyup", handleKeyUp);

        // Initialize WebMIDI
        import("webmidi").then(({ default: webmidi }) => {
            webmidi.enable((err) => {
                if (err) return;
                setMidiSupported(true);

                const updateDevices = () => {
                    setDevices(
                        webmidi.inputs.map((input) => ({
                            id: input.id,
                            name: input.name || input.id,
                        }))
                    );
                };

                updateDevices();
                webmidi.addListener("connected", updateDevices);
                webmidi.addListener("disconnected", updateDevices);
            });
        });

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("keyup", handleKeyUp);
        };
    }, []);

    const handleDeviceChange = useCallback(
        (id: string) => {
            setSelectedDevice(id);
            const keys: number[] = [];

            const listener = (data: number[] | Uint8Array) => {
                const audioEnv = audioEnvRef.current;
                if (audioEnv.dsp) audioEnv.dsp.midiMessage(data);
                if (data[0] === 144 && data[2]) {
                    if (keys.indexOf(data[1]) === -1) keys.push(data[1]);
                    setCurrentNote(data[1]);
                } else if (data[0] === 128 || (data[0] === 144 && !data[2])) {
                    keys.splice(keys.indexOf(data[1]), 1);
                    setCurrentNote(keys.length ? keys[keys.length - 1] : null);
                }
            };

            if (id === "-2") {
                if (key2MidiRef.current) {
                    key2MidiRef.current.handler = listener;
                    key2MidiRef.current.enabled = true;
                }
                return;
            }

            if (key2MidiRef.current) key2MidiRef.current.enabled = false;
            if (id === "-1") return;

            import("webmidi").then(({ default: webmidi }) => {
                const input = webmidi.getInputById(id);
                if (!input) return;
                if (midiInputRef.current) {
                    midiInputRef.current.removeListener("midimessage", "all");
                }
                midiInputRef.current = input;
                input.addListener("midimessage", "all", (e: any) => listener(e.data));
            });
        },
        [audioEnvRef]
    );

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-500">MIDI Input</Label>
                {!midiSupported && (
                    <Badge variant="outline" className="text-[10px] h-4 border-yellow-800 text-yellow-600">
                        not supported
                    </Badge>
                )}
                {currentNote !== null && (
                    <Badge variant="outline" className="text-[10px] h-4 border-zinc-700 text-zinc-300 font-mono">
                        {currentNote}
                    </Badge>
                )}
            </div>
            <Select value={selectedDevice} onValueChange={handleDeviceChange}>
                <SelectTrigger className="h-7 text-xs bg-[#1f1f1f] border-white/[0.08]">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="-1">None</SelectItem>
                    <SelectItem value="-2">Computer Keyboard</SelectItem>
                    {devices.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                            {d.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
